import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import Loading from "../../../../../components/general/Loading";
import {
  handleError,
  isNotBusinessError,
} from "../../../../../utils/handleError";
import { getIndicatorPayments } from "../../../../../rest/indicator";
import { currencyFormat } from "../../../../../utils/CurrencyUtils";
import ExportDialog from "../utils/ExportDialog";
import LastUpdate from "../utils/LastUpdate";
import * as XLSX from "xlsx";
import { buildWorkbookPayments } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorPayments";
import { useClientPdf } from "../utils/useClientPdf";

export default function IndicatorPayments() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(null);
  const [showExport, setShowExport] = useState(false);

  const { pdfRef, exportPdf } = useClientPdf({
    marginMm: 10,
    orientation: "landscape",
    hideSelectors: ['[data-hide-on-export="true"]', ".modal", ".toast"],
  });

  useEffect(() => {
    (async () => {
      try {
        const resp = await getIndicatorPayments();
        const payload = resp?.data || {};
        const item = Array.isArray(payload?.data)
          ? payload.data[0]
          : payload?.data;
        setPayments(item || {});
        setLastUpdate(payload?.lastRefreshedAt || null);
        setRefreshVersion(payload?.refreshVersion || null);
      } catch (err) {
        if (isNotBusinessError(err)) setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Loading />;
  if (error) return handleError(error);

  const fmt = (v) => currencyFormat(v ?? 0);
  const inv = payments?.investmentBreakdown || {};
  const ws = payments?.workstationsBreakdown || {};

  const handleExportExcelFinal = async () => {
    try {
      const wb = await buildWorkbookPayments();
      XLSX.writeFile(wb, "indicadores_pagamentos.xlsx");
      setShowExport(false);
    } catch (e) {
      console.error("[Excel Pagamentos] falhou:", e);
      alert("Falha ao gerar Excel: " + (e?.message || e));
    }
  };

  return (
    <div className="container-fluid" ref={pdfRef}>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="text-secondary mb-0">
            <FormattedMessage
              id="indicator.menu.pagamentos"
              defaultMessage="Pagamentos"
            />
          </h4>
        </Col>
        <Col className="text-end">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowExport(true);
            }}
            style={{ color: "#e5533d", textDecoration: "underline" }}
            data-hide-on-export="true"
          >
            <FormattedMessage
              id="indicator.exportar"
              defaultMessage="Exportar"
            />
          </a>
        </Col>
      </Row>

      <Row xs={1} md={2} className="g-3">
        <Col>
          <div className="card h-100">
            <div className="card-header fw-semibold">Aprovado</div>
            <ul className="list-group list-group-flush">
              <li className="list-group-item d-flex justify-content-between">
                <span>Total aprovado</span>
                <span className="fw-semibold">
                  {fmt(payments?.totalApproved)}
                </span>
              </li>
              <li className="list-group-item d-flex justify-content-between">
                <span>Total aprovado para investimento</span>
                <span className="fw-semibold">
                  {fmt(payments?.totalApprovedInvestment)}
                </span>
              </li>
              <li className="list-group-item d-flex justify-content-between">
                <span>Total aprovado para criação do próprio emprego</span>
                <span className="fw-semibold">
                  {fmt(payments?.totalApprovedWorkstations)}
                </span>
              </li>
            </ul>
          </div>
        </Col>

        <Col>
          <div className="card h-100">
            <div className="card-header fw-semibold">Pago</div>
            <ul className="list-group list-group-flush">
              <li className="list-group-item d-flex justify-content-between">
                <span>Total pago</span>
                <span className="fw-semibold">{fmt(payments?.totalPaid)}</span>
              </li>
              <li className="list-group-item">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">Investimento (Total)</span>
                  <span>{fmt(inv?.total)}</span>
                </div>
                <div className="mt-2 ps-2">
                  <div className="d-flex justify-content-between">
                    <span>Adiantamento</span>
                    <span>{fmt(inv?.advance)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Encerramento</span>
                    <span>{fmt(inv?.closure)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Compensatório</span>
                    <span>{fmt(inv?.compensatory)}</span>
                  </div>
                </div>
              </li>
              <li className="list-group-item">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">
                    Criação do próprio emprego (Total)
                  </span>
                  <span>{fmt(ws?.total)}</span>
                </div>
                <div className="mt-2 ps-2">
                  <div className="d-flex justify-content-between">
                    <span>Adiantamento</span>
                    <span>{fmt(ws?.advance)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Encerramento</span>
                    <span>{fmt(ws?.closure)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Compensatório</span>
                    <span>{fmt(ws?.compensatory)}</span>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </Col>
      </Row>

      <LastUpdate iso={lastUpdate} version={refreshVersion} className="mt-3" />

      <ExportDialog
        show={showExport}
        onHide={() => setShowExport(false)}
        options={[
          {
            onClick: handleExportExcelFinal,
            filename: "indicadores_pagamentos.xlsx",
          },
          {
            onClick: () => exportPdf("indicadores_pagamentos.pdf"),
            filename: "indicadores_pagamentos.pdf",
          },
        ]}
      />
    </div>
  );
}
